import type { CaptionStyle, CaptionTone } from "@nlc/shared-types";

import { MAX_CAPTION_HINT_LENGTH } from "@nlc/shared-types";
import { Loader, Sparkles } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Label, TextArea, TextField } from "@/components/field";
import { Modal } from "@/components/modal";
import { SegmentedControl } from "@/components/segmented-control";
import { toast } from "@/components/toast/toast-queue";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";

import type { DesignFile } from "../design-step/types";
import type { PostType } from "../post-type-step/postTypes";

import { useGenerateCaptions } from "../../../hooks/useGenerateCaptions";

const TONES: { id: CaptionTone; label: string }[] = [
  { id: "friendly", label: "Friendly" },
  { id: "professional", label: "Professional" },
  { id: "playful", label: "Playful" },
  { id: "bold", label: "Bold" },
];

const STYLE_LABELS: Record<CaptionStyle, string> = {
  clean: "Words only",
  emoji: "With emojis",
  hashtags: "With hashtags",
};

export function CaptionGenerator({
  brandId,
  designs,
  onUseCaption,
  postType,
}: {
  brandId: number;
  designs: DesignFile[];
  onUseCaption: (text: string) => void;
  postType: PostType;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hint, setHint] = useState("");
  const [tone, setTone] = useState<CaptionTone>("friendly");
  const { data: brand } = useGetBrandById(brandId);
  const generateCaptions = useGenerateCaptions();
  const captions = generateCaptions.data?.captions;

  function generate() {
    if (!brand) return;

    generateCaptions.mutate(
      {
        brand: {
          audience: brand.basicInformation.audience || null,
          description: brand.description || null,
          industry: brand.basicInformation.industry || null,
          keywords: brand.keywords.map((keyword) => keyword.keyword),
          name: brand.name,
        },
        designs,
        format: postType.format,
        hint: hint.trim() || undefined,
        platform: postType.platform,
        tone,
      },
      {
        onError: (error) => {
          toast.add({ title: error.message, type: "error" }, { timeout: 5000 });
        },
      },
    );
  }

  function pickCaption(text: string) {
    onUseCaption(text);
    setIsOpen(false);
  }

  return (
    <>
      <Button
        className="self-start"
        color="color"
        onPress={() => setIsOpen(true)}
        variant="secondary"
      >
        <Sparkles />
        Generate copy
      </Button>

      <Modal
        classNames={{ modal: "w-full max-w-[640px]" }}
        isDismissable
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <Dialog>
          <DialogHeader className="text-lg font-semibold">
            Generate copy
          </DialogHeader>
          <DialogCloseButton />
          <DialogBody className="gap-xl">
            <TextField
              maxLength={MAX_CAPTION_HINT_LENGTH}
              onChange={setHint}
              value={hint}
            >
              <Label>What&apos;s this post about? (optional)</Label>
              <TextArea
                placeholder="e.g. Black Friday sale, 30% off everything until Sunday"
                rows={2}
              />
            </TextField>

            <div className="flex flex-col gap-sm">
              <span className="text-sm font-medium text-text-secondary">
                Tone
              </span>
              <SegmentedControl<CaptionTone>
                aria-label="Tone"
                className="self-start"
                onChange={setTone}
                options={TONES}
                value={tone}
              />
            </div>

            {generateCaptions.isPending && <WritingIndicator />}
            {!generateCaptions.isPending && captions && (
              <ul
                aria-label="Caption suggestions"
                className="flex flex-col gap-md"
              >
                {captions.map((caption) => (
                  <li
                    className="flex flex-col gap-md rounded-lg border border-border-secondary p-lg"
                    key={caption.style}
                  >
                    <span className="text-xs font-semibold text-text-tertiary uppercase">
                      {STYLE_LABELS[caption.style]}
                    </span>
                    <p className="text-sm whitespace-pre-line text-text-primary">
                      {caption.text}
                    </p>
                    <Button
                      className="self-start"
                      color="gray"
                      onPress={() => pickCaption(caption.text)}
                      size="sm"
                      variant="secondary"
                    >
                      Use this caption
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              isCustomLoading
              isDisabled={!brand || generateCaptions.isPending}
              isLoading={generateCaptions.isPending}
              loadingLabel="Writing captions"
              onPress={generate}
            >
              {generateCaptions.isPending ? (
                <>
                  <Loader className="animate-spin" />
                  Writing...
                </>
              ) : (
                <>
                  <Sparkles />
                  {captions ? "Regenerate" : "Generate"}
                </>
              )}
            </Button>
          </DialogFooter>
        </Dialog>
      </Modal>
    </>
  );
}

// Same typing indicator as the MBC chat, so waiting on AI looks alike across the app.
function WritingIndicator() {
  return (
    <div
      aria-label="Writing your captions"
      className="flex items-center gap-md py-md"
      role="status"
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-utility-brand-100 text-fg-brand-primary">
        <Sparkles className="size-4" />
      </div>
      <div className="flex items-center gap-xs">
        <span className="size-2 rounded-full bg-fg-quaternary motion-safe:animate-bounce" />
        <span className="size-2 rounded-full bg-fg-quaternary [animation-delay:150ms] motion-safe:animate-bounce" />
        <span className="size-2 rounded-full bg-fg-quaternary [animation-delay:300ms] motion-safe:animate-bounce" />
      </div>
      <span className="text-sm text-text-tertiary">Writing your captions…</span>
    </div>
  );
}
